import React from "react";
import { Navigate, useParams } from "react-router-dom";
import Footer from "./Footer";
import SEO from "./SEO";
import industrySolutionsData from "./industrySolutionsData";
import "./IndustrySolutionPage.css";

const IndustrySolutionPage = () => {
  const { solutionSlug } = useParams();

  if (solutionSlug === "optical-crm") {
    return <Navigate to="/optical-crm" replace />;
  }

  const solution = industrySolutionsData[solutionSlug];

  if (!solution) {
    return <Navigate to="/" replace />;
  }

  return (
    <>
      <SEO
        title={`${solution.title} | Industry Solutions`}
        description={solution.seoDescription || solution.shortDescription}
        keywords={solution.seoKeywords || `${solution.title}, industry solutions, IoT solutions, enterprise software`}
        image={solution.seoImage}
        url={`/industry-solutions/${solutionSlug}`}
        type="article"
        product={{
          name: solution.title,
          description: solution.shortDescription,
          operatingSystem: "Web",
          features: [
            ...(solution.highlights || []),
            ...(solution.capabilityPillars || []),
            ...(solution.environmentPillars || [])
          ].join(", ")
        }}
      />
      <section className={`industry-solution-page ${solution.themeVariant || ""}`}>
        <div className="industry-solution-hero">
          <div className="industry-solution-hero-content">
            <div className="industry-solution-kicker">Fracto Industry Solutions</div>
            <h1>{solution.title}</h1>
            <p>{solution.intro}</p>
            <div className="ready-proof-chips">
              <span>Pre-configured Modules</span>
              <span>API-ready Integration</span>
              <span>Cloud + On-prem Support</span>
              <span>Pilot in 2-4 Weeks</span>
            </div>
            <div className="industry-solution-hero-actions">
              <a href="/#contact" className="industry-solution-primary-btn">
                Contact Sales
              </a>
            </div>
          </div>
          <div className="industry-solution-hero-visual">
            <div className="industry-solution-icon-badge">
              <img src={solution.icon} alt="" aria-hidden="true" />
              <span>Fracto</span>
            </div>
            <img src={solution.heroImage} alt={solution.title} className="industry-solution-hero-image" />
          </div>
        </div>

        <div className="industry-solution-section ready-deploy-section">
          <h2>Ready-to-Deploy Solution</h2>
          <p className="industry-solution-section-lead">
            Start with a production-focused foundation instead of building every component from scratch. Our team helps you go from pilot to rollout with prebuilt modules and a proven implementation path.
          </p>
          <div className="industry-solution-modules">
            <article className="industry-solution-module-card">
              <h3>Out-of-the-box Stack</h3>
              <p>Dashboards, alert templates, user roles, and baseline reports are pre-configured for fast onboarding.</p>
            </article>
            <article className="industry-solution-module-card">
              <h3>Implementation Path</h3>
              <p>Discovery and mapping, guided pilot deployment, then structured scale-up across sites and teams.</p>
            </article>
            <article className="industry-solution-module-card">
              <h3>Enterprise Integration</h3>
              <p>Connect devices and business systems through APIs and protocol-friendly interfaces for long-term interoperability.</p>
            </article>
          </div>
          <div className="ready-metrics">
            <div className="ready-metric">
              <strong>2-6 weeks</strong>
              <span>Typical pilot timeline</span>
            </div>
            <div className="ready-metric">
              <strong>3-10 days</strong>
              <span>Common integration window</span>
            </div>
            <div className="ready-metric">
              <strong>30-90 days</strong>
              <span>ROI visibility in operations</span>
            </div>
          </div>
        </div>

        {solution.environmentPillars && (
          <div className="industry-solution-section">
            <h2>Environmental Priority Areas</h2>
            <div className="environment-pillars">
              {solution.environmentPillars.map((pillar) => (
                <div className="environment-pillar-chip" key={pillar}>
                  {pillar}
                </div>
              ))}
            </div>
          </div>
        )}

        {solution.capabilityPillars && (
          <div className="industry-solution-section">
            <h2>Platform Capabilities</h2>
            <div className="environment-pillars">
              {solution.capabilityPillars.map((item) => (
                <div className="environment-pillar-chip" key={item}>
                  {item}
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="industry-solution-section">
          <h2>Key Highlights</h2>
          <div className="industry-solution-highlights">
            {solution.highlights.map((item) => (
              <div className="industry-solution-highlight" key={item}>
                <p>{item}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="industry-solution-section">
          <h2>Core Modules</h2>
          <div className="industry-solution-modules">
            {solution.modules.map((module) => (
              <article className="industry-solution-module-card" key={module.name}>
                <h3>{module.name}</h3>
                <p>{module.text}</p>
              </article>
            ))}
          </div>
        </div>

        {solution.diagramImage && (
          <div className="industry-solution-section">
            <h2>{solution.architectureTitle || "System Architecture"}</h2>
            {solution.architectureDescription && (
              <p className="industry-solution-section-lead">{solution.architectureDescription}</p>
            )}
            <div className="industry-solution-diagram-wrap">
              <img src={solution.diagramImage} alt={`${solution.title} architecture diagram`} className="industry-solution-diagram" />
            </div>
            {solution.architectureSteps && (
              <div className="industry-solution-highlights architecture-points">
                {solution.architectureSteps.map((step) => (
                  <div className="industry-solution-highlight" key={step}>
                    <p>{step}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {solution.communicationOptions && (
          <div className="industry-solution-section">
            <h2>Connectivity and Field Reliability</h2>
            <div className="industry-solution-highlights">
              {solution.communicationOptions.map((option) => (
                <div className="industry-solution-highlight" key={option}>
                  <p>{option}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {solution.impactCards && (
          <div className="industry-solution-section">
            <h2>Business Impact</h2>
            <div className="industry-solution-modules">
              {solution.impactCards.map((card) => (
                <article className="industry-solution-module-card" key={card.title}>
                  <h3>{card.title}</h3>
                  <p>{card.text}</p>
                </article>
              ))}
            </div>
          </div>
        )}
      </section>
      <Footer />
    </>
  );
};

export default IndustrySolutionPage;
